import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { postFetcher, deleteFetcher, getFetcher } from "../../api/fetchers";
import { endpoints } from "../../api/endpoints";
import { parseStatics } from "../function/parseStatics";
import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  exp?: number;
  [key: string]: any; // diğer alanlar da olabilir
}

interface User {
  companyName: string;
  identityNo: string;
  taxNumber: string;
  kepAddress: string;
  taxOffice: string;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  createdAt: string;
  ePara: number | null;
  glnCode: string;
  district: string;
  openAddress: string;
  password: string;
  profilePicture: string;
  referenceCode: string;
  status: boolean;
  updatedAt: string;
  verificationCode: string;
  pharmacyName: string;
  city: number;
  name?: string
  businessPackageId: any
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;
  favoriteProducts: string[];
  addFavoriteProduct: (productId: string) => Promise<void>;
  removeFavoriteProduct: (productId: string) => Promise<void>;
  fetchFavoriteProducts: (userId: string) => Promise<void>
  isLoading: boolean;
  isAuth: boolean;
  getIsAuth: any;
  falseShowLoginPopup: any;
  trueShowLoginPopup: any;
  showLoginPopup: any;
  setFavoriteProducts: any;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [favoriteProducts, setFavoriteProducts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [showLoginPopup, setShowLoginPopup] = useState<boolean>(false);

  useEffect(() => {
    const initializeUser = async () => {
      const savedUser = localStorage.getItem("user");
      if (savedUser) {
        const parsedUser = parseStatics(savedUser);
        setUserState(parsedUser);
        await fetchFavoriteProducts(parsedUser.id);
      }
      setIsLoading(false);
    };

    initializeUser();
  }, []);

  const isTokenExpired = (token: string): boolean => {
    try {
      const decoded = jwtDecode<JwtPayload>(token);

      const now = Date.now() / 1000;
      if (!decoded.exp) {
        //console.log("Token has no exp field");
        return true;
      }

      const expired = decoded.exp < now;
      //console.log("Now:", now, "Exp:", decoded.exp, "Expired:", expired);
      return expired;
    } catch (e) {
      console.error("Invalid token", e);
      return true;
    }
  };


  const getIsAuth = async () => {
    const savedUser = localStorage.getItem("user");
    const token = localStorage.getItem("access-token");

    if (savedUser && token && !isTokenExpired(token)) {
      setIsAuth(true);
    } else {
      setIsAuth(false);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  };

  const falseShowLoginPopup = () => {
    setShowLoginPopup(false);
  };

  const trueShowLoginPopup = () => {
    setShowLoginPopup(true);
  };

  const fetchFavoriteProducts = async (userId: string) => {
    try {
      const response = await getFetcher(
        `${endpoints.user.getFavorites}/${userId}`
      );
      if (response?.result) {
        const favoriteIds = response.favoriteProducts.map(
          (favoriteProduct: any) => favoriteProduct.productId
        );
        setFavoriteProducts(favoriteIds);
      }
    } catch (error) {
      console.error("Error fetching favorite products:", error);
    }
  };

  const setUser = useCallback((newUser: User | null) => {
    if (newUser) {
      localStorage.setItem("user", JSON.stringify(newUser));
      fetchFavoriteProducts(newUser.id);
    } else {
      localStorage.removeItem("user");
      setFavoriteProducts([]);
    }
    setUserState(newUser);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("access-token");
    sessionStorage.removeItem("access-token");
    localStorage.removeItem("jwtToken");
    sessionStorage.removeItem("jwtToken");
  }, [setUser]);

  const addFavoriteProduct = useCallback(
    async (productId: string) => {
      if (user) {
        try {
          const response = await postFetcher(
            endpoints.user.addFavoriteProduct,
            {
              arg: {
                userId: user.id,
                productId,
              },
            }
          );
          if (response) {
            setFavoriteProducts((prev) => [...prev, productId]);
          }
        } catch (error) {
          console.error("Error adding favorite product:", error);
          throw error;
        }
      }
    },
    [user]
  );

  const removeFavoriteProduct = useCallback(
    async (productId: string) => {
      if (user) {
        try {
          const response = await deleteFetcher(
            endpoints.user.deleteFavoriteProduct
              .replace("{userId}", user.id)
              .replace("{productId}", productId)
          );
          if (response) {
            setFavoriteProducts((prev) =>
              prev.filter((id) => id !== productId)
            );
          }
        } catch (error) {
          console.error("Error removing favorite product:", error);
          throw error;
        }
      }
    },
    [user]
  );

  const contextValue = {
    user,
    setUser,
    logout,
    favoriteProducts,
    addFavoriteProduct,
    removeFavoriteProduct,
    isLoading,
    getIsAuth,
    isAuth,
    falseShowLoginPopup,
    trueShowLoginPopup,
    showLoginPopup,
    fetchFavoriteProducts,
    setFavoriteProducts
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
